import { proxy, snapshot, type Snapshot } from "valtio";
import { deepClone, subscribeKey } from "valtio/utils";
import type { Annotations } from "./annotations";
import { clearArray } from "./utils";

type HistoryItem = {
    features: Snapshot<Annotations["state"]>["features"];
};

export class Journal {
    public state = proxy({
        writeTs: Date.now(),
        batchTs: Date.now(),
        squashTs: Date.now(),
    });

    public write() {
        this.state.writeTs = Date.now();
    }

    public batch() {
        this.state.batchTs = Date.now();
    }

    public squash() {
        this.state.squashTs = Date.now();
    }
}

export class History {
    public state = proxy({
        past: [] as HistoryItem[],
        future: [] as HistoryItem[],
    });

    private start: number | null = null;

    private readonly annotations: Annotations;

    constructor(annotations: Annotations, journal: Journal) {
        this.annotations = annotations;

        subscribeKey(journal.state, "writeTs", () => this.onWrite(), true);
        subscribeKey(journal.state, "batchTs", () => this.onBatch(), true);
        subscribeKey(journal.state, "squashTs", () => this.onSquash(), true);
    }

    public undo() {
        const previous = this.state.past.pop();

        if (previous) {
            const { features } = snapshot(this.annotations.state);

            this.state.future.push({ features });

            for (const id of Object.keys(features)) {
                delete this.annotations.state.features[id];
            }

            Object.assign(this.annotations.state.features, deepClone(previous.features));
        }
    }

    public redo() {
        const next = this.state.future.pop();

        if (next) {
            const { features } = snapshot(this.annotations.state);

            this.state.past.push({ features });

            for (const id of Object.keys(features)) {
                delete this.annotations.state.features[id];
            }

            Object.assign(this.annotations.state.features, deepClone(next.features));
        }
    }

    public onSquash() {
        if (this.start !== null) {
            this.state.past.splice(this.start);
            this.start = null;
        }
    }

    public onBatch() {
        this.onWrite();
        this.start = this.state.past.length;
    }

    public onWrite() {
        const { features } = snapshot(this.annotations.state);
        const previous = this.state.past[this.state.past.length - 1];

        if (this.compare(previous, { features })) {
            return;
        }

        this.state.past.push({ features });
        clearArray(this.state.future);
    }

    public reset() {
        clearArray(this.state.past);
        clearArray(this.state.future);
        this.start = null;
    }

    private compare(a: HistoryItem, b: HistoryItem) {
        return JSON.stringify(a) === JSON.stringify(b);
    }
}
